import { Link } from "react-router-dom";

const LAST_UPDATED = "February 12, 2026";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="kicker text-[hsl(var(--accent))]">{title}</h2>
    <div className="mt-3 text-[hsl(var(--ink-soft))] leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function Privacy() {
  return (
    <div className="pt-32">
      <section className="mx-auto max-w-3xl px-6 md:px-10 py-16">
        <div className="kicker">Privacy Policy</div>
        <h1 className="font-display text-4xl md:text-6xl leading-[1.05] mt-6">
          How we handle your <span className="italic">personal information</span>.
        </h1>
        <p className="mt-4 text-sm text-[hsl(var(--ink-soft))]">Last updated: {LAST_UPDATED}</p>

        <div className="mt-8 border border-[hsl(var(--line))] p-5 text-sm text-[hsl(var(--ink-soft))] bg-[hsl(var(--bg-alt))]">
          <strong className="text-[hsl(var(--ink))]">Compliance disclaimer.</strong> Shobha
          with Shubhra LLC is a Wisconsin, USA limited liability company that offers services
          only to residents of Wisconsin. This Privacy Policy is designed to meet our
          obligations under the California Consumer Privacy Act (CCPA) as amended by the CPRA,
          the U.S. Federal Trade Commission Act, and general United States privacy best
          practice. If you access the Site from outside the United States you do so on your
          own initiative and are responsible for compliance with your local laws.
        </div>

        <Section title="1. Who we are">
          <p>
            This Privacy Policy applies to the website{" "}
            <strong>shobhawithshubhra.com</strong> operated by{" "}
            <strong>Shobha with Shubhra LLC</strong> ("we", "us", "our"), a Wisconsin-based
            wedding and event planning studio founded by Shubhra Prasad.
          </p>
          <p>
            Privacy questions? Email{" "}
            <a
              className="text-[hsl(var(--accent))] underline"
              href="mailto:contact@shobhawithshubhra.com"
            >
              contact@shobhawithshubhra.com
            </a>
            .
          </p>
        </Section>

        <Section title="2. Information we collect">
          <p>
            <strong>Information you give us.</strong> When you use our contact form, chat
            widget or Vision Studio generator, we collect the name, email, phone number, event
            details, and any message or note you provide.
          </p>
          <p>
            <strong>Information collected automatically (with consent).</strong> If you accept
            our cookie banner, we log basic technical data: IP address, approximate location
            (city / state derived from IP), browser type, referring URL, pages you view, and
            interactions such as scroll depth and form-start events. This is used solely to
            understand which pages are useful and to reduce spam.
          </p>
          <p>
            <strong>Cookies and similar technologies.</strong> Details of every cookie and
            browser-storage key we use are listed in our{" "}
            <Link to="/cookie-policy" className="text-[hsl(var(--accent))] underline">
              Cookie Policy
            </Link>
            . Non-essential cookies load only after you click "Accept all".
          </p>
          <p>
            <strong>Sensitive categories.</strong> We do not knowingly collect sensitive
            personal information such as health, precise geolocation, financial account
            numbers, biometrics, or immigration status. Please do not send such information
            via the site.
          </p>
        </Section>

        <Section title="3. How we use your information">
          <ul className="list-disc list-outside pl-5 space-y-2">
            <li>Respond to your enquiries and consultation requests.</li>
            <li>Operate, maintain and improve the website.</li>
            <li>Prevent abuse, spam and fraudulent submissions.</li>
            <li>Generate the AI mood-board previews you request.</li>
            <li>Send occasional updates about events, only if you have specifically opted in.</li>
          </ul>
          <p>
            We do <strong>not</strong> sell, rent or "share" (as defined by the CCPA/CPRA)
            your personal information. We do not use your data to train AI models.
          </p>
        </Section>

        <Section title="4. Service providers">
          <p>
            To run the site we use a small number of United States-based service providers.
            Each receives only the data needed to do its job and is contractually bound to
            protect it.
          </p>
          <ul className="list-disc list-outside pl-5 space-y-2">
            <li>
              <strong>Formspree</strong> — routes contact-form and mood-board submissions to
              our inbox.
            </li>
            <li>
              <strong>ip-api.com</strong> — resolves IP addresses into approximate city /
              state.
            </li>
            <li>
              <strong>Anthropic (Claude)</strong> — powers our AI chat assistant and
              mood-board captions.
            </li>
            <li>
              <strong>Google Gemini via Emergent</strong> — generates mood-board images.
              Content you type in the vibe field is sent to the model to produce images.
            </li>
            <li>
              <strong>MongoDB Atlas / Emergent hosting</strong> — stores enquiries, chat
              transcripts, vision boards and site analytics.
            </li>
          </ul>
        </Section>

        <Section title="5. How long we keep your data">
          <ul className="list-disc list-outside pl-5 space-y-2">
            <li>Enquiries and chat transcripts — up to 36 months from last contact.</li>
            <li>Vision boards — 90 days, unless saved with an email (then 24 months).</li>
            <li>Analytics logs — 24 months.</li>
            <li>Consent records — 6 years (record-keeping audit trail).</li>
          </ul>
          <p>You can request earlier deletion at any time (see "Your rights").</p>
        </Section>

        <Section title="6. Your rights (California residents — CCPA / CPRA)">
          <p>If you are a California resident, you have the right to:</p>
          <ul className="list-disc list-outside pl-5 space-y-2">
            <li>Know what personal information we collect and how we use it.</li>
            <li>Request that we delete personal information we hold about you.</li>
            <li>Request correction of inaccurate personal information.</li>
            <li>
              Opt out of the "sale" or "sharing" of personal information —{" "}
              <strong>we do not sell or share</strong>. Our "Reject non-essential" cookie
              button acts as our Global Privacy Control response.
            </li>
            <li>Limit use of sensitive personal information (we do not collect any).</li>
            <li>Non-discrimination for exercising your rights.</li>
          </ul>
          <p>
            To exercise any of these rights, email{" "}
            <a
              className="text-[hsl(var(--accent))] underline"
              href="mailto:contact@shobhawithshubhra.com"
            >
              contact@shobhawithshubhra.com
            </a>
            . We respond within 45 days.
          </p>
        </Section>

        <Section title="7. Your rights (all other U.S. visitors)">
          <p>
            Even if you are not a California resident, you may contact us at the email above
            to request access to, correction of, or deletion of any personal information we
            hold about you. We will respond in a reasonable timeframe.
          </p>
        </Section>

        <Section title="8. Children">
          <p>
            Our services are not directed to children under 13. In accordance with the U.S.
            Children's Online Privacy Protection Act (COPPA), we do not knowingly collect
            information from anyone under 13. If you believe a child has provided data,
            contact us and we will delete it.
          </p>
        </Section>

        <Section title="9. Security">
          <p>
            We use industry-standard measures to protect your data in transit (HTTPS, HSTS)
            and at rest (encrypted database, access-controlled admin dashboard behind
            httpOnly cookie authentication with rate-limited login). No online service can
            guarantee absolute security, but we work continuously to keep your information
            safe and will notify affected users of any breach in accordance with applicable
            U.S. state breach-notification laws.
          </p>
        </Section>

        <Section title="10. Automated decision-making &amp; AI">
          <p>
            The site includes an AI chat assistant ("Ayaan") and an AI-powered mood-board
            generator. These tools produce suggestions only and do <strong>not</strong> make
            legally significant decisions about you. Every quotation, contract and creative
            decision is reviewed by a human at Shobha with Shubhra.
          </p>
        </Section>

        <Section title="11. Changes to this policy">
          <p>
            We may update this policy from time to time. Material changes will be posted with
            a new "Last updated" date and, where required by law, we will re-request consent
            via the cookie banner.
          </p>
        </Section>

        <div className="mt-16 text-sm flex flex-wrap gap-6">
          <Link to="/cookie-policy" className="text-[hsl(var(--accent))] underline">
            View Cookie Policy →
          </Link>
          <Link to="/terms" className="text-[hsl(var(--accent))] underline">
            View Terms of Service →
          </Link>
          <Link to="/" className="text-[hsl(var(--accent))] underline">
            ← Back to home
          </Link>
        </div>
      </section>
    </div>
  );
}
